import { Injectable } from '@angular/core';
import { AiAgent } from '../models/ai-agents.interface';
import { map, Observable, tap } from 'rxjs';
import { ApiClientAiAgentsService } from 'src/app/api/api-client-ai-agents.service';
import { AiAgentEnum, AiAgentStatusEnum } from 'src/app/api/models/ai-agents/ai-agents.enum';
import { AiAgentsStore } from '../state/ai-agents.store';
import { AiAgentAssessmentResource } from 'src/app/api/models/ai-agents/ai-agents.interface';

@Injectable({
  providedIn: 'root'
})
export class AiAgentsService {
    private agentsInitialized = false;

    constructor(
        private apiClientAiAgentsService: ApiClientAiAgentsService,
        private aiAgentsStore: AiAgentsStore,
    ) {}

    initAgents(): void {
        // Idempotent: called from the AI-agents module AND from pages that need agent
        // availability (e.g. system page) — connect the store streams only once.
        if (this.agentsInitialized) return;
        this.agentsInitialized = true;
        this.aiAgentsStore.loadAgents();
    }

    selectAiAgents(): Observable<AiAgent[]> {
        return this.aiAgentsStore.select('agents').pipe(
            map(agents => Array.from(agents.values()))
        );
    }

    isValidSetup(miraId: AiAgentEnum): boolean {
        const agent = this.getAiAgentById(miraId);
        switch(miraId) {
            case AiAgentEnum.TiaCreate:
                return !!(agent.setupValues as AiAgentAssessmentResource)?.templatePageId;
            default:
                return true;
        }
    }

    saveAiAgent(miraId: AiAgentEnum, aiAgent: Partial<AiAgent>): Observable<AiAgent> {
        return this.apiClientAiAgentsService.patchAiAgent(miraId, aiAgent).pipe(
            map(agent => ({...aiAgent, ...agent} as AiAgent)),
            tap(agent => this.aiAgentsStore.updateAgent(miraId, agent))
        );
    }

    getAiAgentById(miraId: AiAgentEnum): AiAgent {
        return this.aiAgentsStore.signal('agents')()?.get(miraId);
    }

    selectAiAgentById(miraId: AiAgentEnum): Observable<AiAgent> {
        return this.aiAgentsStore.select('agents').pipe(
            map(agents => agents.get(miraId))
        );
    }

    runAiAgentSample(miraId: AiAgentEnum, systemIds: string[]): Observable<void> {
        return this.apiClientAiAgentsService.triggerAiAgentSample(miraId, systemIds);
    }

    stopAiAgent(miraId: AiAgentEnum, aiAgent: Partial<AiAgent>): Observable<AiAgent> {
        return this.apiClientAiAgentsService.patchAiAgent(miraId, {
            status: AiAgentStatusEnum.Disabled
        }).pipe(
            map(agent => ({...aiAgent, ...agent} as AiAgent)),
            tap(agent => this.aiAgentsStore.updateAgent(miraId, agent))
        );
    }

    startAiAgent(miraId: AiAgentEnum, aiAgent: Partial<AiAgent>): Observable<AiAgent> {
        return this.apiClientAiAgentsService.patchAiAgent(miraId, {
            status: AiAgentStatusEnum.Enabled
        }).pipe(
            map(agent => ({...aiAgent, ...agent} as AiAgent)),
            tap(agent => this.aiAgentsStore.updateAgent(miraId, agent))
        );
    }

    triggerAiAgentSample(miraId: AiAgentEnum, systemIds: string[]): Observable<void> {
        return this.apiClientAiAgentsService.triggerAiAgentSample(miraId, systemIds);
    }
}